import React, { useState } from "react";
import { SITE } from "./config";
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/apiClient";
import Seo from "./Seo";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    company: "",
    phone: "",
    email: "",
    city: "",
    message: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/leads", { ...form, source: "site" });
      setSent(true);
      setForm({ name: "", company: "", phone: "", email: "", city: "", message: "" });
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const mapsHref = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(SITE.address.full)}`;

  return (
    <div>
      <Seo
        title="Contact — FB Environnement Saint-Victoret"
        description="Contactez FB Environnement à Saint-Victoret (13730). 07 83 48 15 72 — contact@fbenvironnement.fr. Devis rapide, gros volumes, partenariat BTP."
        path="/contact"
      />
      <section className="bg-gradient-to-br from-[#1F6B2E] to-[#0f3a18] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-16">
          <span className="inline-block bg-[#9DCB2E] text-[#0f3a18] font-black uppercase tracking-[0.15em] text-xs px-3 py-1.5 rounded-full">
            Contact
          </span>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight mt-5">
            Pour plus d&apos;informations, contactez-nous
          </h1>
          <p className="text-stone-100 mt-3 max-w-2xl">
            Une question, un besoin spécifique, un volume important ? On vous répond rapidement.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14 grid lg:grid-cols-2 gap-10">
        {/* Form */}
        <div className="bg-white rounded-3xl border border-stone-100 p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.05)]">
          {sent ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-[#9DCB2E] text-[#0f3a18] grid place-items-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8" strokeWidth={2.8} />
              </div>
              <h2 className="font-heading font-black text-2xl text-[#1F6B2E]">Message envoyé !</h2>
              <p className="text-stone-600 mt-2">
                Merci, nous revenons vers vous dans les meilleurs délais.
              </p>
              <button
                type="button"
                onClick={() => setSent(false)}
                className="mt-6 inline-flex items-center gap-2 bg-stone-100 hover:bg-stone-200 text-stone-700 font-bold px-5 py-2.5 rounded-full"
              >
                Envoyer un autre message
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="contact-form">
              <h2 className="font-heading font-black text-2xl text-stone-900 mb-2">Demande d&apos;informations</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Votre nom *" value={form.name} onChange={(v) => update("name", v)} required testid="lead-name" />
                <Field label="Société" value={form.company} onChange={(v) => update("company", v)} testid="lead-company" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Téléphone *" type="tel" value={form.phone} onChange={(v) => update("phone", v)} required testid="lead-phone" />
                <Field label="Email" type="email" value={form.email} onChange={(v) => update("email", v)} testid="lead-email" />
              </div>
              <Field label="Ville" value={form.city} onChange={(v) => update("city", v)} testid="lead-city" />
              <div>
                <label className="block text-sm font-bold text-stone-700 mb-2">Votre message *</label>
                <textarea
                  required
                  data-testid="lead-message"
                  rows={4}
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  placeholder="Type de déchet, volume estimé, fréquence... toute info utile."
                  className="w-full bg-stone-50 border border-stone-200 focus:bg-white focus:border-[#1F6B2E] focus:ring-4 focus:ring-[#9DCB2E]/20 outline-none rounded-2xl px-4 py-3 transition-all"
                />
              </div>

              {error && (
                <div className="text-sm bg-rose-50 text-rose-700 border border-rose-200 rounded-2xl px-4 py-3">{error}</div>
              )}

              <button
                type="submit"
                disabled={busy}
                data-testid="contact-submit"
                className="w-full bg-[#1F6B2E] hover:bg-[#175522] disabled:opacity-60 text-white font-bold py-3.5 rounded-full transition-all flex items-center justify-center gap-2 shadow-lg shadow-[#1F6B2E]/20 active:scale-[0.98]"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={2.8} />}
                {busy ? "Envoi..." : "Envoyer mon message"}
              </button>
            </form>
          )}
        </div>

        {/* Infos */}
        <div className="space-y-4">
          <InfoCard icon={<Phone className="w-6 h-6" strokeWidth={2.5} />} title="Téléphone">
            <a href={`tel:${SITE.phoneRaw}`} className="text-[#1F6B2E] font-black text-xl hover:underline">
              {SITE.phone}
            </a>
            <p className="text-sm text-stone-500 mt-1">Du lundi au vendredi, 7h — 17h non-stop</p>
          </InfoCard>

          <InfoCard icon={<Mail className="w-6 h-6" strokeWidth={2.5} />} title="Email">
            <a href={`mailto:${SITE.email}`} className="text-[#1F6B2E] font-bold hover:underline break-all">
              {SITE.email}
            </a>
          </InfoCard>

          <InfoCard icon={<MapPin className="w-6 h-6" strokeWidth={2.5} />} title="Adresse">
            <div className="font-bold">{SITE.address.street}</div>
            <div>{SITE.address.postal} {SITE.address.city}</div>
            <a
              href={mapsHref}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center gap-1.5 text-[#1F6B2E] font-bold text-sm hover:underline"
            >
              Itinéraire Google Maps →
            </a>
          </InfoCard>

          <InfoCard icon={<Clock className="w-6 h-6" strokeWidth={2.5} />} title="Horaires">
            <div className="mt-1 -mx-1">
              <div className="text-xs uppercase tracking-[0.18em] font-black text-stone-500">
                Du lundi au vendredi
              </div>
              <div className="mt-2 flex items-baseline gap-2 flex-wrap">
                <span className="font-heading font-black text-3xl sm:text-4xl tracking-tight text-stone-900 leading-none">
                  7h
                </span>
                <span className="text-stone-400 font-heading font-black text-xl">—</span>
                <span className="font-heading font-black text-3xl sm:text-4xl tracking-tight text-stone-900 leading-none">
                  17h
                </span>
                <span className="ml-1 inline-flex items-center gap-1 bg-[#9DCB2E] text-[#0f3a18] font-black text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-full">
                  Non-stop
                </span>
              </div>
              <div className="mt-3 text-xs text-stone-500 font-medium">
                Fermé samedi et dimanche · Sans rendez-vous
              </div>
            </div>
          </InfoCard>
        </div>
      </section>

      {/* Map */}
      <section className="pb-12 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto rounded-3xl overflow-hidden shadow-xl border border-stone-100">
          <iframe
            title="Plan FB Environnement"
            src={`https://maps.google.com/maps?q=${encodeURIComponent(SITE.address.full)}&output=embed`}
            className="w-full h-80 sm:h-96 border-0"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </section>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, required, testid }) {
  return (
    <div>
      <label className="block text-sm font-bold text-stone-700 mb-1">{label}</label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-stone-50 border border-stone-200 focus:bg-white focus:border-[#1F6B2E] focus:ring-4 focus:ring-[#9DCB2E]/20 outline-none rounded-2xl px-4 py-3 transition-all font-medium"
      />
    </div>
  );
}

function InfoCard({ icon, title, children }) {
  return (
    <div className="bg-white rounded-3xl border border-stone-100 p-5 shadow-[0_4px_20px_rgb(0,0,0,0.04)]">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-[#1F6B2E] text-white grid place-items-center shrink-0">{icon}</div>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-wider font-black text-stone-500">{title}</div>
          <div className="mt-1 text-stone-900">{children}</div>
        </div>
      </div>
    </div>
  );
}
